/* eslint-disable sonarjs/no-duplicate-string */
import { datadogLogs } from '@datadog/browser-logs';
import { HubException } from '@shared/exceptions';
import Cookies from 'js-cookie';
import { ACCESS_TOKEN, REFRESH_TOKEN } from '@shared/const';
import { useGlobalStore } from '@modules/core/store';
import { resetAmplitudeIdentity } from '@shared/analytics/amplitude';
import { Fetcher } from '@huspy/forge/shared';
import { removeAuthCookies } from '@shared/utils';
import { IS_SPAIN_ENV } from '@modules/core/utils';
import { jwtDecode } from 'jwt-decode';

const ContentTypeApplicationJson = 'application/json';

type GetNewTokenResponse = {
  access_token: string;
  accessToken: string;
  expires_in: number;
  token_type: string;
};

export class BaseFetcher extends Fetcher {
  COGNITO_REFRESH_TOKEN_ENDPOINT = `${COSMOS_API_URL}/api/v1/identity/token/refresh`;

  KEYCLOAK_REFRESH_TOKEN_ENDPOINT = `${USER_SERVICE_URL}/api/v1/auth/token/refresh`;

  IDENTITY_CLIENT_ID = IS_SPAIN_ENV ? 'bayteca-portal-client' : 'broker-portal-client';

  // eslint-disable-next-line class-methods-use-this, sonarjs/cognitive-complexity
  public async request<T extends {}>(
    url: RequestInfo | URL,
    method: string,
    body: Record<string, unknown> | null,
    headers: HeadersInit,
    signal: AbortController['signal'],
    _sendAuthorizationHeaders: boolean = true
  ): Promise<T> {
    const defaultHeaders: Record<string, any> = {};

    if (!(body instanceof FormData)) {
      defaultHeaders['Content-Type'] = ContentTypeApplicationJson;
    }

    if (_sendAuthorizationHeaders) {
      const token = Cookies.get(ACCESS_TOKEN);
      defaultHeaders.Authorization = `Bearer ${token}`;
    }

    const config: RequestInit = {
      method,
      ...(signal || undefined),
      headers: {
        ...defaultHeaders,
        ...headers,
      },
    };

    if (body instanceof FormData) {
      config.body = body;
    } else if (body) {
      config.body = JSON.stringify(body);
    } else {
      config.body = null;
    }

    datadogLogs.logger.info(`Calling: ${method} ${url.toString()}`, {
      payload: body,
      request: { method, url: url.toString() },
    });
    let response = await fetch(url, config);
    try {
      if ([401, 403].includes(response.status)) {
        response = await this.tryReAuthThenRefetch(url, config);
      }
      if (response.status === 503) {
        window.location.replace('/maintenance');
      }

      if (!response.ok) {
        const err = await response.json();
        if (response.status === 500 && IS_SPAIN_ENV) {
          err.message = 'Algo salió mal. Por favor, contacta a Bayteca.';
        }
        throw new HubException(
          err.message ?? 'Hub API error',
          'HubError',
          response.status,
          JSON.stringify(err),
          err
        );
      }

      let responseData: T | {} = {};

      if (response.headers.get('Content-Type')?.includes('application/json')) {
        responseData = await response.json();
      }

      if (response.headers.get('Content-Type')?.includes('application/octet-stream')) {
        responseData = await response.blob();
      }

      if (response.headers.get('Content-Type')?.includes('application/pdf')) {
        const reader = response.body!.getReader();
        const chunks = [];

        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();
          if (done) {
            break;
          }
          chunks.push(value);
        }

        const pdfBlob = new Blob(chunks, { type: 'application/pdf' });

        responseData = pdfBlob;
      }

      datadogLogs.logger.info(`Success: ${method} ${url.toString()}`, {
        payload: responseData,
        request: {
          method,
          url: url.toString(),
          body,
        },
      });
      return responseData as T;
    } catch (error: any) {
      datadogLogs.logger.error(`${method} ${url.toString()}`, {
        error,
        request: {
          method,
          url: url.toString(),
          body,
        },
      });
      throw error;
    }
  }

  private async tryReAuthThenRefetch(url: RequestInfo | URL, config: RequestInit): Promise<Response> {
    const accessToken = await this.reauth();

    if (accessToken) {
      Cookies.set(ACCESS_TOKEN, accessToken, { domain: COOKIE_SUBDOMAIN });
      const newConfig: RequestInit = {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, newConfig);
      if (!response.ok) {
        const refetch_error = await response.json();
        throw new HubException(
          refetch_error.message ?? 'Hub API error',
          'HubError',
          response.status,
          JSON.stringify(refetch_error),
          refetch_error
        );
      }
      return response;
    }
    throw new HubException(
      'Hub API error',
      'HubError',
      400,
      JSON.stringify({}),
      {}
    );
  }

  private static resetAuthData() {
    resetAmplitudeIdentity();
    datadogLogs.clearUser();
    removeAuthCookies(true);
    useGlobalStore.getState().setIsLoggedIn(false);
  }

  private async reauth() {
    const refreshToken = Cookies.get(REFRESH_TOKEN);
    const accessToken = Cookies.get(ACCESS_TOKEN);

    if (!refreshToken || !accessToken) {
      BaseFetcher.resetAuthData();
      return null;
    }

    const decodedToken = jwtDecode(accessToken);
    const isCognito = Boolean(decodedToken.iss?.includes('cognito'));

    const res = await this.performTokenRefresh(refreshToken, isCognito);

    if (!res.ok) {
      BaseFetcher.resetAuthData();
      return null;
    }

    const payload = await res.json() as GetNewTokenResponse;
    return payload.access_token || payload.accessToken;
  }

  private async performTokenRefresh(refreshToken: string, isCognito: boolean): Promise<Response> {
    const url = isCognito
      ? this.COGNITO_REFRESH_TOKEN_ENDPOINT
      : this.KEYCLOAK_REFRESH_TOKEN_ENDPOINT;

    const headers: Record<string, string> = { 'Content-Type': ContentTypeApplicationJson };
    if (!isCognito) {
      headers['x-identity-client'] = this.IDENTITY_CLIENT_ID;
    }

    const body = isCognito
      ? { refresh_token: refreshToken }
      : { refreshToken };

    return fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
  }
}
